import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import {
  EMAIL_TEMPLATE_OPTIONS,
  type InvitationEmailTemplateLiteral,
} from "@/v2-backoffice-client/invitation/invitation.constants"
import emailPreviewStandard from "@/v2-backoffice-client/invitation/create/emailPreviewStandard.png"
import emailPreviewNewApp from "@/v2-backoffice-client/invitation/create/emailPreviewNewApp.png"
import emailPreviewMigration from "@/v2-backoffice-client/invitation/create/emailPreviewMigration.png"

/**
 * Rendered previews of the exact email each template value sends (nb copy,
 * from v2-sendgrid's copy sets) — the operator sees what the invitee gets
 * before anything is sent (FUS-927 workshop ask). The cards crop to the
 * email's TOP (logo, title, intro — where the two designs differ); the
 * expand control opens the full render.
 */
const THUMBNAILS: Record<InvitationEmailTemplateLiteral, string> = {
  STANDARD: emailPreviewStandard,
  NEW_APP: emailPreviewNewApp,
  MIGRATION: emailPreviewMigration,
}

type InvitationCreateTemplateFieldProps = {
  value: InvitationEmailTemplateLiteral
  onChange(value: InvitationEmailTemplateLiteral): void
}

/**
 * The email-design picker of the invite dialog: one card per selectable
 * template, cropped preview first — the choice applies to every invitee in
 * the batch, like Language, and is stored on each invitation so resends
 * match.
 */
function InvitationCreateTemplateField(
  props: InvitationCreateTemplateFieldProps,
) {
  const { value, onChange } = props

  return (
    <div className="invitation-create-form-template">
      <span className="invitation-create-form-label">Email design</span>
      <div
        role="radiogroup"
        aria-label="Email design"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        {EMAIL_TEMPLATE_OPTIONS.map((option) => {
          const selected = option.value === value
          return (
            // The expand control cannot live INSIDE the selection button
            // (nested interactive elements) — it overlays the thumbnail as
            // a sibling. Not the shared <Button>: its ripple CSS pins
            // position:relative + overflow:hidden, which defeats the
            // overlay placement.
            <div key={option.value} className="group relative">
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onChange(option.value)}
                className={cn(
                  "flex h-full w-full flex-col overflow-hidden rounded-lg border-2 text-left transition",
                  selected
                    ? "border-primary"
                    : "border-border hover:border-muted-foreground/40",
                )}
              >
                {/* The email renders on white; padding keeps the card's own
                    dark background as a frame between the (amber) selection
                    border and the white render, so neither state puts white
                    flush against the border. */}
                <div className="h-36 w-full overflow-hidden border-b border-border p-2">
                  <img
                    src={THUMBNAILS[option.value]}
                    alt={`Preview of the “${option.label}” email`}
                    className="h-full w-full rounded object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-1 p-3">
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <span
                      aria-hidden
                      className={cn(
                        "inline-block size-3 shrink-0 rounded-full border-2",
                        selected
                          ? "border-primary bg-primary"
                          : "border-muted-foreground/50",
                      )}
                    />
                    {option.label}
                  </span>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {option.description}
                  </p>
                </div>
              </button>

              <button
                type="button"
                aria-label={`View the “${option.label}” email at full size`}
                title="View full size"
                className="absolute right-2 top-2 rounded-md bg-black/50 p-1.5 text-white opacity-0 transition hover:bg-black/75 focus-visible:opacity-100 group-hover:opacity-100"
                onClick={() => {
                  window.open(THUMBNAILS[option.value], "_blank", "noopener")
                }}
              >
                <Icon aria-hidden src="expand" size={14} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default InvitationCreateTemplateField
